'use client';
import { useState } from 'react';
import NextImage from 'next/image';
import { cn } from 'lib/utils';
import { Icon, Link, Tag } from 'ui';
import { type TestimonialCardsProps } from './index';

const TestimonialCardsFull = ({ cards }: TestimonialCardsProps) => {
  const [activeCard, setActiveCard] = useState(0);
  const cardCount = cards.length;

  // Render null if less than 2 cards
  if (cardCount < 2) {
    return null;
  }

  return (
    <div className="max-lg:hidden h-125 flex gap-4">
      {cards.map((card, index) => {
        const isActiveCard = activeCard === index;

        return (
          <div
            key={index}
            role="button"
            tabIndex={0}
            aria-pressed={isActiveCard}
            onMouseEnter={() => setActiveCard(index)}
            onFocus={() => setActiveCard(index)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setActiveCard(index);
              }
            }}
            className={cn('grow shrink-0 flex flex-row-reverse', {
              'min-w-60': cardCount === 2,
              'min-w-40 xl:min-w-60': cardCount === 3,
            })}
          >
            {/* Card Image */}
            <div className={cn('size-full relative overflow-hidden')}>
              {/* Background Image */}
              <div
                className={cn(
                  'absolute inset-0 flex flex-col justify-end p-8 rounded-r-lg overflow-hidden',
                  {
                    'rounded-l-lg': !isActiveCard,
                  }
                )}
              >
                <NextImage
                  src={card.image.src}
                  alt={card.image.alt || ''}
                  fill={true}
                  className="absolute z-1 inset-0 size-full object-cover"
                  role="presentation"
                />

                {/* Logo */}
                {card.logo && (
                  <>
                    <NextImage
                      src={card.logo.src}
                      alt={`${card.authorOrg || 'Company'} logo`}
                      width={card.logo.width}
                      height={card.logo.height}
                      className="relative z-3 max-w-full h-auto"
                    />

                    {/* Color Gradient */}
                    <div
                      className="absolute z-2 bottom-0 left-0 w-full h-1/3 bg-gradient-to-t from-black/65 to-transparent"
                      aria-hidden="true"
                    />
                  </>
                )}
              </div>
            </div>

            {/* Card Content */}
            <div
              className={cn(
                'shrink-0 overflow-hidden transition-all duration-300 w-full',
                {
                  'w-0': !isActiveCard,
                  'w-90': isActiveCard && cardCount === 2,
                  'w-80 xl:w-90': isActiveCard && cardCount === 3,
                }
              )}
            >
              <article
                className={cn('size-full p-8 bg-stone-800 rounded-l-lg', {
                  'w-90': cardCount === 2,
                  'w-80 xl:w-90': cardCount === 3,
                })}
              >
                <div
                  className={cn(
                    'h-full flex flex-col delay-400 transition-all duration-300',
                    {
                      'opacity-100 translate-x-0': isActiveCard,
                      'opacity-0 translate-x-10': !isActiveCard,
                    }
                  )}
                >
                  <div className="grow">
                    {!!card.tag && <Tag>{card.tag}</Tag>}
                  </div>

                  <blockquote className="flex flex-col gap-4">
                    <p className="text-h5">
                      {!card.hideLeftQuote && (
                        <>
                          &ldquo; <br />
                        </>
                      )}
                      {card.content}
                      {!card.hideRightQuote && <>&rdquo;</>}
                    </p>

                    <footer className="flex flex-col gap-8">
                      {(!!card.author ||
                        !!card.authorTitle ||
                        !!card.authorOrg) && (
                        <cite className="text-p2 font-bold not-italic text-neutral-200 flex flex-col opacity-85">
                          {(card.author || card.authorTitle) && (
                            <span>
                              {card.author && <span>{card.author}</span>}
                              {card.authorTitle && (
                                <>
                                  {card.author && ', '}
                                  <span>{card.authorTitle}</span>
                                </>
                              )}
                              {card.authorOrg && (
                                <>
                                  {(card.author || card.authorTitle) && <br />}
                                  <span>{card.authorOrg}</span>
                                </>
                              )}
                            </span>
                          )}
                        </cite>
                      )}

                      {!!card.link?.href && (
                        <div className="text-p2">
                          <Link
                            href={card.link.href}
                            target={card.link.target}
                            variant="neutral"
                          >
                            <span>{card.link.text}</span>
                            <Icon
                              name="carousel-arrow-right"
                              className="inline-block align-middle ml-1.5 w-4 h-auto"
                            />
                          </Link>
                        </div>
                      )}
                    </footer>
                  </blockquote>
                </div>
              </article>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TestimonialCardsFull;
